<template>
  <div class="modern-management-report" :style="backgroundStyles">
    <!-- Header con controles de tema -->
    <div class="header-section">
      <div class="title-container">
        <h1 class="main-title">
          <i class="mdi mdi-chart-line"></i>
          Reporte de Gerencia
        </h1>
        <div class="theme-controls">
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
                class="theme-btn"
                :style="{ color: currentTheme.primary }"
              >
                <i class="mdi mdi-palette"></i>
              </v-btn>
            </template>
            <v-card class="theme-selector">
              <v-card-title class="pb-2">Personalizar Colores</v-card-title>
              <v-card-text>
                <div class="color-options">
                  <div 
                    v-for="theme in themeOptions" 
                    :key="theme.name"
                    class="color-option"
                    :class="{ active: selectedTheme === theme.name }"
                    @click="changeTheme(theme.name)"
                  >
                    <div class="color-preview">
                      <div class="color-dot" :style="{ backgroundColor: theme.primary }"></div>
                      <div class="color-dot" :style="{ backgroundColor: theme.secondary }"></div>
                      <div class="color-dot" :style="{ backgroundColor: theme.accent }"></div>
                    </div>
                    <span class="theme-name">{{ theme.displayName }}</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-menu>
        </div>
      </div>
    </div>

    <!-- Tarjeta principal -->
    <v-card class="main-card" :style="cardStyles">
      <!-- Sección de filtros -->
      <div class="filters-section" :style="{ background: `linear-gradient(135deg, ${currentTheme.primary}15, ${currentTheme.secondary}15)` }">
        <div class="filters-header">
          <h2 class="filters-title">
            <i class="mdi mdi-filter-variant" :style="{ color: currentTheme.primary }"></i>
            Criterios de Búsqueda
          </h2>
          <v-chip
            :color="currentTheme.info"
            text-color="white"
            small
            v-if="items.length > 0"
          >
            {{ items.length }} registros encontrados
          </v-chip>
        </div>

        <v-row class="filters-row">
          <v-col cols="12" md="3">
            <div class="filter-container">
              <label class="filter-label">
                <i class="mdi mdi-account-search mr-1"></i>
                Trabajador
              </label>
              <!-- <s-toolbar-person
                ref="DNI"
                v-model="filter.PrsDocumentNumber"
                @returnPerson="returnPerson($event)"
                returnInmediately
                :autofocus="false"
                noEdit
                noCamera
                type="text"
                class="modern-input"
              /> -->
              <s-text
                label="Nombre o DNI"
                v-model="filter.PrsDocumentNumber"
                outlined
                dense
                prepend-inner-icon="mdi-account-search"
                clearable
                @keyupEnter="pagination()"
              ></s-text>
            </div>
          </v-col>
          
          <v-col cols="12" md="3">
            <div class="filter-container">
              <label class="filter-label">
                <i class="mdi mdi-office-building mr-1"></i>
                Área
              </label>
              <s-select-area
                label="Seleccionar área"
                v-model="filter.AreID"
                clearable
                noDefault
                class="modern-select"
                full
              />
            </div>
          </v-col>

          <v-col cols="12" md="3">
            <div class="filter-container">
              <label class="filter-label">
                <i class="mdi mdi-list-status mr-1"></i>
                Estado
              </label>
              <s-select
                label="Seleccionar estado"
                item-value="value"
                item-text="text"
                :items="itemsStatus"
                v-model="filter.Status"
                clearable
              ></s-select>
            </div>
          </v-col>
          
          <v-col cols="12" md="3" class="filter-actions">
            <v-btn
              :color="currentTheme.primary"
              @click="pagination()"
              class="search-btn"
              elevation="2"
              dark
            >
              <i class="mdi mdi-magnify mr-2"></i>
              Buscar
            </v-btn>
            <v-btn
              outlined
              :color="currentTheme.secondary"
              @click="clearFilters()"
              class="clear-btn"
            >
              <i class="mdi mdi-refresh mr-2"></i>
              Limpiar
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- Estadísticas rápidas -->
      <div v-if="items.length > 0" class="stats-section">
        <v-row>
          <v-col cols="6" sm="3">
            <div class="stat-card" :style="statCardStyles">
              <div class="stat-icon">
                <i class="mdi mdi-account-group" :style="{ color: currentTheme.primary }"></i>
              </div>
              <div class="stat-content">
                <p class="stat-value">{{ items.length }}</p>
                <p class="stat-label">Total Empleados</p>
              </div>
            </div>
          </v-col>
          <v-col cols="6" sm="3">
            <div class="stat-card" :style="statCardStyles2(currentTheme.success)">
              <div class="stat-icon">
                <i style="color: white;" class="mdi mdi-check-circle"></i>
              </div>
              <div class="stat-content">
                <p class="stat-value">{{ getStatusCount('Correcto') }}</p>
                <p class="stat-labelE">Correctos</p>
              </div>
            </div>
          </v-col>
          <v-col cols="6" sm="3">
            <div class="stat-card" :style="statCardStyles2(currentTheme.warning)">
              <div class="stat-icon">
                <i style="color: white;" class="mdi mdi-clock-alert"></i>
              </div>
              <div class="stat-content">
                <p class="stat-value">{{ getStatusCount('Seguimiento') }}</p>
                <p class="stat-labelE">Seguimiento</p>
              </div>
            </div>
          </v-col>
          <v-col cols="6" sm="3">
            <div class="stat-card" :style="statCardStyles2(currentTheme.error)">
              <div class="stat-icon">
                <i style="color: white;" class="mdi mdi-alert-circle"></i>
              </div>
              <div class="stat-content">
                <p class="stat-value">{{ getStatusCount('Vencidas') }}</p>
                <p class="stat-labelE">Vencidas</p>
              </div>
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- Tabla de datos -->
      <div class="table-section">
        <div class="table-header" v-if="items.length > 0">
          <h3 class="table-title">Detalle del Reporte</h3>
          <div class="table-actions">
            <v-btn
              :color="currentTheme.success"
              small
              outlined
              @click="exportData()"
            >
              <i class="mdi mdi-download mr-1"></i>
              Exportar
            </v-btn>
          </div>
        </div>

        <div class="modern-table-container" v-if="items.length > 0">
          <v-data-table
            :headers="headers"
            item-key="DNI"
            v-model="selected"
            @click:row="selectedCheckSlot($event)"
            :items="items"
            :items-per-page="25"
            class="modern-table"
            :footer-props="{
              showCurrentPage: true,
              showFirstLastPage: true,
              itemsPerPageOptions: [25, 50, 100, -1],
            }"
          >
            <template v-slot:item.ESTADO="{ item }">
              <v-chip 
                small
                :color="getStatusColor(item.ESTADO)"
                :text-color="getStatusTextColor(item.ESTADO)"
                class="status-chip"
              >
                <v-icon class="mr-1">{{getStatusIcon(item.ESTADO)}}</v-icon>
                {{ item.ESTADO }}
              </v-chip>
            </template>

            <template v-slot:[`item.DIAS_VENDIDOS`]="{ item }">
              <v-chip
                v-if="item['DIAS VENDIDOS'] > 0 || item['DIAS_VENDIDOS'] > 0"
                small
                color="amber darken-2"
                text-color="white"
              >
                <v-icon left small>mdi-cash</v-icon>
                {{ item['DIAS VENDIDOS'] || item['DIAS_VENDIDOS'] }}d vend.
              </v-chip>
              <span v-else class="grey--text">-</span>
            </template>
          </v-data-table>
        </div>

        <!-- Estado vacío -->
        <div v-else class="empty-state">
          <div class="empty-icon">
            <i class="mdi mdi-file-search-outline" :style="{ color: currentTheme.primary }"></i>
          </div>
          <h3>No hay datos disponibles</h3>
          <p>Utiliza los filtros para buscar información específica</p>
          <v-btn
            :color="currentTheme.primary"
            @click="pagination()"
            class="mt-4"
            dark
          >
            <i class="mdi mdi-refresh mr-2"></i>
            Cargar Datos
          </v-btn>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import sToolbarPerson from "@/components/Utils/Person/sToolbarPerson";
import VacationsReportManagementService from '@/services/HumanResource/VacationsReportManagementService';
import GenericService from "@/services/General/GenericService";

export default {
    components: {
        sToolbarPerson
    },
    data() {
        return {
            itemsStatus: [
              { text: 'Correcto', value: 'Correcto' },
              { text: 'Seguimiento', value: 'Seguimiento' },
              { text: 'Vencidas', value: 'Vencidas' }
            ],
            selected: [],
            items: [],
            filter: {
                PrsDocumentNumber: "",
                AreID: null,
                Status: null
            },
            headers: [
                { text: "GERENCIA", value: "Gerencia" },
                { text: "APELLIDOS Y NOMBRES", value: "NtpFullName" },
                { text: "DNI", value: "PrsDocumentNumber" },
                { text: "AREA", value: "AreName" },
                { text: "CARGO", value: "PstName" },
                { text: "F. DE INGRESO", value: "CtrAdmissionDate" },
                { text: "MES", value: "MonthCtrAdmissionDate" },
                { text: "COSTO", value: "TypeSituationName" },
                { text: "PERIODO 1", value: "Period1" },
                { text: "PERIODO 2", value: "Period2" },
                { text: "PERIODO 3", value: "Period3" },
                { text: "ESTADO", value: "ESTADO" },
            ],
            selectedTheme: 'blue',
            themeOptions: [
                {
                    name: 'blue',
                    displayName: 'Azul Corporativo',
                    primary: '#1976D2',
                    secondary: '#42A5F5',
                    accent: '#FFC107',
                    success: '#4CAF50',
                    warning: '#FF9800',
                    error: '#F44336',
                    info: '#2196F3'
                },
                {
                    name: 'purple',
                    displayName: 'Púrpura Moderno',
                    primary: '#7B1FA2',
                    secondary: '#AB47BC',
                    accent: '#E91E63',
                    success: '#4CAF50',
                    warning: '#FF9800',
                    error: '#F44336',
                    info: '#9C27B0'
                },
                {
                    name: 'green',
                    displayName: 'Verde Natural',
                    primary: '#388E3C',
                    secondary: '#66BB6A',
                    accent: '#FF5722',
                    success: '#4CAF50',
                    warning: '#FF9800',
                    error: '#F44336',
                    info: '#009688'
                },
                {
                    name: 'orange',
                    displayName: 'Naranja Energético',
                    primary: '#F57C00',
                    secondary: '#FFB74D',
                    accent: '#E91E63',
                    success: '#4CAF50',
                    warning: '#FF9800',
                    error: '#F44336',
                    info: '#FF9800'
                },
                {
                    name: 'dark',
                    displayName: 'Oscuro Elegante',
                    primary: '#424242',
                    secondary: '#616161',
                    accent: '#00BCD4',
                    success: '#4CAF50',
                    warning: '#FF9800',
                    error: '#F44336',
                    info: '#009688'
                },
                {
                    name: 'pinkSoft',
                    displayName: 'Rosado Pastel',
                    primary: '#F48FB1',
                    secondary: '#FCE4EC',
                    accent: '#EC407A',
                    success: '#4CAF50',
                    warning: '#FF9800',
                    error: '#F44336',
                    info: '#F48FB1'
                }
            ]
        }
    },
    computed: {
        currentTheme() {
            return this.themeOptions.find(theme => theme.name === this.selectedTheme);
        },
        backgroundStyles() {
            return {
                background: `linear-gradient(135deg, #f5f7fa 0%, ${this.currentTheme.secondary}40 100%)`
            };
        },
        cardStyles() {
            return {
                boxShadow: `0 8px 32px ${this.currentTheme.primary}20`,
                border: `1px solid ${this.currentTheme.primary}10`
            };
        },
        statCardStyles() {
            return {
                background: `linear-gradient(135deg, ${this.currentTheme.primary}08, ${this.currentTheme.secondary}08)`,
                border: `1px solid ${this.currentTheme.primary}20`,
            };
        }
    },
    mounted() {
        this.loadTheme();
    },
    methods: {
        statCardStyles2(color) {
            return {
                background: `${color}`,
                border: `1px solid ${this.currentTheme.primary}20`,
            };
        },
        loadTheme() {
            const savedTheme = localStorage.getItem('managementReportTheme');
            if (savedTheme && this.themeOptions.find(t => t.name === savedTheme)) {
                this.selectedTheme = savedTheme;
            }
        },
        changeTheme(themeName) {
            this.selectedTheme = themeName;
            localStorage.setItem('managementReportTheme', themeName);
        },
        pagination() {
            console.log(this.filter);
            VacationsReportManagementService.pagination(this.filter, this.$fun.getUserID()).then((r) => {
                if (r.status === 200) {
                    if (r.data.length > 0) {
                        this.items = r.data;
                        this.headers = Object.keys(r.data[0]).map(key => ({
                            text: key.replace(/_/g, ' ').toUpperCase(),
                            value: key,
                            align: key.includes('Date') ? 'center' : 'left'
                        }));
                    } else {
                        this.items = [];
                        this.headers = [
                            { text: "GERENCIA", value: "Gerencia" },
                            { text: "APELLIDOS Y NOMBRES", value: "NtpFullName" },
                            { text: "DNI", value: "PrsDocumentNumber" },
                            { text: "AREA", value: "AreName" },
                            { text: "CARGO", value: "PstName" },
                            { text: "F. DE INGRESO", value: "CtrAdmissionDate" },
                            { text: "MES", value: "MonthCtrAdmissionDate" },
                            { text: "COSTO", value: "TypeSituationName" },
                            { text: "PERIODO 1", value: "Period1" },
                            { text: "PERIODO 2", value: "Period2" },
                            { text: "PERIODO 3", value: "Period3" },
                            { text: "ESTADO", value: "ESTADO" },
                        ];
                    }
                }
            });
        },
        returnPerson(item) {
            console.log(item);
            if (item != null) {
                this.filter.PrsDocumentNumber = item.PrsDocumentNumber;
            }
        },
        clearFilters() {
            this.filter = {
                PrsDocumentNumber: "",
                AreID: null
            };
            this.items = [];
        },
        selectedCheckSlot(obj) {
            console.log(obj);
            if (obj !== null) {
                this.selected = [];
                this.selected.push(obj);
            }
        },
        getStatusCount(status) {
            return this.items.filter(item => item.ESTADO === status).length;
        },
        getStatusColor(status) {
            switch (status) {
                case 'Correcto': return this.currentTheme.success;
                case 'Seguimiento': return this.currentTheme.warning;
                case 'Vencidas': return this.currentTheme.error;
                default: return 'grey';
            }
        },
        getStatusTextColor(status) {
            return 'white';
        },
        getStatusIcon(status) {
            switch (status) {
                case 'Correcto': return 'mdi-check-circle';
                case 'Seguimiento': return 'mdi-clock-alert';
                case 'Vencidas': return 'mdi-alert-circle';
                default: return 'mdi-help-circle';
            }
        },
        formatDataForExcel(headers, data) {
            return data.map(row => {
                let formattedRow = {};
                headers.forEach(h => {
                    let val = row[h.value];
                    if (val === undefined) {
                        const correctKey = Object.keys(row).find(
                            k => k.toLowerCase() === h.value.toLowerCase()
                        );
                        if (correctKey) {
                            val = row[correctKey];
                        }
                    }
                    formattedRow[h.text] = val;
                });
                return formattedRow;
            });
        },
        exportData() {
            // Aquí puedes implementar la lógica de exportación
            console.log('Exportando datos...', this.items);
            const formattedData = this.formatDataForExcel(this.headers, this.items);

            GenericService.getConvertExcel(formattedData, this.$fun.getUserID())
            .then(resp => {
                this.$fun.downloadFileSH(resp.data, this.$const.TypeFile.EXCEL, `ReporteGerencia`);
            });
        }
    },
    created() {
        this.pagination();
    }
}
</script>

<style scoped>
.modern-management-report {
  padding: 20px;
  border-radius: 10px;
  min-height: 100vh;
}

.header-section {
  margin-bottom: 24px;
}

.title-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.main-title {
  font-size: 2.5rem;
  font-weight: 300;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.main-title i {
  font-size: 2.8rem;
}

.theme-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.theme-btn {
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.theme-selector {
  min-width: 300px;
}

.color-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.color-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.color-option:hover {
  background: rgba(0, 0, 0, 0.04);
}

.color-option.active {
  background: rgba(25, 118, 210, 0.1);
  border-color: #1976D2;
}

.color-preview {
  display: flex;
  gap: 4px;
}

.color-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.theme-name {
  font-weight: 500;
}

.main-card {
  border-radius: 16px !important;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
}

.filters-section {
  padding: 24px;
  border-radius: 16px 16px 0 0;
}

.filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
}

.filters-title {
  font-size: 1.4rem;
  font-weight: 500;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.filters-row {
  align-items: end;
}

.filter-container {
  margin-bottom: 8px;
}

.filter-label {
  display: block;
  font-size: 0.9rem;
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 8px;
}

.filter-actions {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: flex-end;
}

.search-btn, .clear-btn {
  font-weight: 500 !important;
  text-transform: none !important;
  border-radius: 8px !important;
}

.stats-section {
  padding: 16px 24px;
  background: rgba(255, 255, 255, 0.5);
}

.stat-card {
  padding: 20px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 16px;
  height: 100%;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.stat-icon i {
  font-size: 2rem;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 1.8rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 4px 0;
  line-height: 1;
}

.stat-labelE {
  font-size: 0.85rem;
  color: white;
  margin: 0;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-label {
  font-size: 0.85rem;
  color: #7f8c8d;
  margin: 0;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table-section {
  padding: 24px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}

.table-title {
  font-size: 1.4rem;
  font-weight: 500;
  color: #2c3e50;
  margin: 0;
}

.table-actions {
  display: flex;
  gap: 8px;
}

.modern-table-container {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.modern-table {
  background: rgba(255, 255, 255, 0.9) !important;
}

.data-row {
  transition: all 0.2s ease;
}

.data-row:hover {
  background: rgba(0, 0, 0, 0.02) !important;
}

.data-row.selected {
  background: rgba(25, 118, 210, 0.08) !important;
}

.status-chip {
  font-weight: 500 !important;
  border-radius: 16px !important;
}

.date-cell {
  font-family: 'Roboto Mono', monospace;
  font-size: 0.9rem;
  padding: 2px 8px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  display: inline-block;
}

.dni-cell {
  font-family: 'Roboto Mono', monospace;
}

.regular-cell {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #7f8c8d;
}

.empty-icon i {
  font-size: 5rem;
  margin-bottom: 16px;
}

.empty-state h3 {
  font-size: 1.5rem;
  font-weight: 500;
  margin: 0 0 8px 0;
  color: #2c3e50;
}

.empty-state p {
  font-size: 1rem;
  margin: 0;
}

@media (max-width: 768px) {
  .modern-management-report {
    padding: 12px;
  }
  
  .main-title {
    font-size: 2rem;
  }
  
  .filter-actions {
    justify-content: center;
    flex-direction: column;
  }
  
  .search-btn, .clear-btn {
    width: 100%;
  }
  
  .table-header {
    flex-direction: column;
    text-align: center;
  }
}
</style>